/**
 * HR Dashboard Page
 *
 * HR overview with tab navigation: Beoordelingen (evaluations) and Verslagen (assessment reports).
 */

import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEvaluations, useUpdateEvaluationStatus, useDeleteEvaluation } from '../evaluations/useEvaluations';
import { ScoreDisplay, percentageToScoreLevel } from '../evaluations/wizard/ScoreInput';
import { AllAgreementsSection } from '../agreements/AllAgreementsSection';
import { useAllAssessmentReports } from '../team/useAssessmentReports';
import type { EvaluationStatus, AssessmentReportStatus } from '../../lib/evaluationsApi';
import { OrgScheduleOverview } from '../werkrooster/components/OrgScheduleOverview';
import { NmbrsSyncStatus } from '../nmbrs/components/NmbrsSyncStatus';
import { NmbrsLinkPanel } from '../nmbrs/components/NmbrsLinkPanel';
import { NmbrsUnlinkedPanel } from '../nmbrs/components/NmbrsUnlinkedPanel';
import { NmbrsLeaveBalanceCard } from '../nmbrs/components/NmbrsLeaveBalanceCard';
import { NmbrsLeaveOverview } from '../nmbrs/components/NmbrsLeaveOverview';
import { NmbrsLeaveRequestForm } from '../nmbrs/components/NmbrsLeaveRequestForm';
import { NmbrsLeaveRequestList } from '../nmbrs/components/NmbrsLeaveRequestList';
import { NmbrsLeaveLinkPanel } from '../nmbrs/components/NmbrsLeaveLinkPanel';
import { NmbrsAbsenceOverview } from '../nmbrs/components/NmbrsAbsenceOverview';
import { NmbrsTeamCalendar } from '../nmbrs/components/NmbrsTeamCalendar';
import { NmbrsSyncDashboard } from '../nmbrs/components/NmbrsSyncDashboard';
import { NmbrsSyncHistory } from '../nmbrs/components/NmbrsSyncHistory';

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Concept', color: 'text-yellow-800', bgColor: 'bg-yellow-100' },
  submitted: { label: 'Ingediend', color: 'text-blue-800', bgColor: 'bg-blue-100' },
  in_review: { label: 'In Review', color: 'text-purple-800', bgColor: 'bg-purple-100' },
  final: { label: 'Definitief', color: 'text-green-800', bgColor: 'bg-green-100' },
  cancelled: { label: 'Geannuleerd', color: 'text-gray-600', bgColor: 'bg-gray-100' },
};

const REPORT_STATUS_CONFIG: Record<AssessmentReportStatus, { label: string; color: string }> = {
  concept: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  ingediend: { label: 'Ingediend', color: 'bg-blue-100 text-blue-800' },
  definitief: { label: 'Definitief', color: 'bg-green-100 text-green-800' },
};

const ASSESSMENT_LABELS: Record<string, { label: string; color: string }> = {
  onvoldoende: { label: 'Onvoldoende', color: 'text-red-700 bg-red-50' },
  onder_verwachting: { label: 'Onder verwachting', color: 'text-orange-700 bg-orange-50' },
  naar_verwachting: { label: 'Naar verwachting', color: 'text-green-700 bg-green-50' },
  boven_verwachting: { label: 'Boven verwachting', color: 'text-blue-700 bg-blue-50' },
};

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatPeriod(start: string, end: string) {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return `${startDate.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })} - ${endDate.toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}`;
}

export function HRDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const activeTab = tab === 'verslagen' ? 'verslagen'
    : tab === 'werkroosters' ? 'werkroosters'
    : tab === 'nmbrs' ? 'nmbrs'
    : 'beoordelingen';

  const nmbrsSubTab = searchParams.get('nmbrsTab') || 'koppelingen';

  const setTab = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (tab === 'beoordelingen') {
      newParams.delete('tab');
      newParams.delete('nmbrsTab');
    } else {
      newParams.set('tab', tab);
      if (tab !== 'nmbrs') {
        newParams.delete('nmbrsTab');
      }
    }
    setSearchParams(newParams);
  };

  const setNmbrsSubTab = (subTab: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (subTab === 'koppelingen') {
      newParams.delete('nmbrsTab');
    } else {
      newParams.set('nmbrsTab', subTab);
    }
    setSearchParams(newParams);
  };

  // Evaluations state
  const { data: evaluations, isLoading } = useEvaluations();
  const updateStatus = useUpdateEvaluationStatus();
  const deleteEvaluation = useDeleteEvaluation();

  const [statusFilter, setStatusFilter] = useState<EvaluationStatus | 'all'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'score' | 'status'>('date');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);

  // Assessment reports state
  const [reportStatusFilter, setReportStatusFilter] = useState<AssessmentReportStatus | 'all'>('all');
  const [reportYearFilter, setReportYearFilter] = useState<number | 'all'>('all');
  const [reportSearch, setReportSearch] = useState('');

  const apiFilters = useMemo(() => ({
    status: reportStatusFilter !== 'all' ? reportStatusFilter : undefined,
    year: reportYearFilter !== 'all' ? reportYearFilter : undefined,
  }), [reportStatusFilter, reportYearFilter]);

  // Unfiltered query for stats and year dropdown (F6/F7 fix)
  const { data: allReportsData } = useAllAssessmentReports();
  const unfilteredReports = allReportsData?.data || [];

  // Filtered query for the table
  const { data: reportsData, isLoading: reportsLoading } = useAllAssessmentReports(apiFilters);
  const tableReports = reportsData?.data || [];

  // Client-side search filter on the filtered table data
  const filteredReports = useMemo(() => {
    if (!reportSearch.trim()) return tableReports;
    const search = reportSearch.toLowerCase();
    return tableReports.filter(r =>
      r.employeeName?.toLowerCase().includes(search)
    );
  }, [tableReports, reportSearch]);

  // Extract available years from ALL reports (unfiltered)
  const availableYears = useMemo(() => {
    const years = new Set(unfilteredReports.map(r => new Date(r.periodeStart).getFullYear()));
    return [...years].sort((a, b) => b - a);
  }, [unfilteredReports]);

  // Report stats from ALL reports (unfiltered)
  const reportStats = useMemo(() => ({
    total: unfilteredReports.length,
    concept: unfilteredReports.filter(r => r.status === 'concept').length,
    ingediend: unfilteredReports.filter(r => r.status === 'ingediend').length,
    definitief: unfilteredReports.filter(r => r.status === 'definitief').length,
  }), [unfilteredReports]);

  const filteredEvaluations = evaluations?.data
    ?.filter((e) => statusFilter === 'all' ? true : e.status === statusFilter)
    ?.sort((a, b) => {
      if (sortBy === 'date') {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      if (sortBy === 'score') {
        const scoreA = a.totalScore ? parseFloat(a.totalScore) : 0;
        const scoreB = b.totalScore ? parseFloat(b.totalScore) : 0;
        return scoreB - scoreA;
      }
      return a.status.localeCompare(b.status);
    }) || [];

  // Calculate statistics
  const evalData = evaluations?.data || [];
  const evalsWithScore = evalData.filter((e) => e.totalScore);
  const stats = {
    total: evalData.length,
    submitted: evalData.filter((e) => e.status === 'submitted').length,
    inReview: evalData.filter((e) => e.status === 'in_review').length,
    final: evalData.filter((e) => e.status === 'final').length,
    avgScore: evalsWithScore.length > 0
      ? evalsWithScore.reduce((sum, e) => sum + parseFloat(e.totalScore || '0'), 0) / evalsWithScore.length
      : 0,
  };

  const handleBulkApprove = async () => {
    for (const id of selectedIds) {
      const evaluation = evaluations?.data?.find((e) => e.id === id);
      if (evaluation?.status === 'submitted') {
        await updateStatus.mutateAsync({ id, status: 'in_review' });
      } else if (evaluation?.status === 'in_review') {
        await updateStatus.mutateAsync({ id, status: 'final' });
      }
    }
    setSelectedIds([]);
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredEvaluations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEvaluations.map((e) => e.id));
    }
  };

  const handleDeleteEvaluation = async () => {
    if (!deleteConfirmId) return;
    try {
      await deleteEvaluation.mutateAsync(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch {
      // Error handled by mutation
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Beoordelingen laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="hr-dashboard-page" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">HR Overzicht</h1>
          <p className="mt-1 text-sm text-gray-500">
            Bekijk en beheer alle beoordelingen en verslagen in de organisatie.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-8">
            <button
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'beoordelingen'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('beoordelingen')}
            >
              Beoordelingen
            </button>
            <button
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'verslagen'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('verslagen')}
            >
              Verslagen
            </button>
            <button
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'werkroosters'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('werkroosters')}
            >
              Werkroosters
            </button>
            <button
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'nmbrs'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setTab('nmbrs')}
            >
              Nmbrs
            </button>
          </nav>
        </div>

        {/* === Beoordelingen Tab === */}
        {activeTab === 'beoordelingen' && (
          <>
            {/* Statistics */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Totaal</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <p className="text-3xl font-bold text-blue-700">{stats.submitted}</p>
                <p className="text-sm text-blue-600">Wacht op review</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
                <p className="text-3xl font-bold text-purple-700">{stats.inReview}</p>
                <p className="text-sm text-purple-600">In review</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <p className="text-3xl font-bold text-green-700">{stats.final}</p>
                <p className="text-sm text-green-600">Afgerond</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                <p className="text-3xl font-bold text-gray-700">{stats.avgScore.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Gem. Score</p>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="mb-4 flex items-center gap-3">
              <button
                onClick={() => {
                  const url = selectedIds.length > 0
                    ? `/hr/vergelijken?ids=${selectedIds.join(',')}`
                    : '/hr/vergelijken';
                  navigate(url);
                }}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Vergelijken
              </button>
            </div>

            {/* Filters & Actions */}
            <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {/* Status Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Status:</span>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as EvaluationStatus | 'all')}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Alle</option>
                    <option value="submitted">Ingediend</option>
                    <option value="in_review">In Review</option>
                    <option value="final">Definitief</option>
                    <option value="draft">Concept</option>
                  </select>
                </div>

                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Sorteer:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'date' | 'score' | 'status')}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="date">Datum</option>
                    <option value="score">Score</option>
                    <option value="status">Status</option>
                  </select>
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedIds.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{selectedIds.length} geselecteerd</span>
                  <button
                    onClick={handleBulkApprove}
                    disabled={updateStatus.isPending}
                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    Goedkeuren
                  </button>
                  <button
                    onClick={() => setSelectedIds([])}
                    className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                  >
                    Annuleren
                  </button>
                </div>
              )}
            </div>

            {/* Evaluations Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === filteredEvaluations.length && filteredEvaluations.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Medewerker
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Manager
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Periode
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Score
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEvaluations.map((evaluation) => (
                    <tr
                      key={evaluation.id}
                      className={`hover:bg-gray-50 ${selectedIds.includes(evaluation.id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(evaluation.id)}
                          onChange={() => handleToggleSelect(evaluation.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <span className="text-blue-600 text-xs font-medium">
                              {evaluation.employeeName
                                ? evaluation.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                : `M${evaluation.employeeId}`}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {evaluation.employeeName || `Medewerker #${evaluation.employeeId}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {evaluation.managerName || `Manager #${evaluation.managerId}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })} -{' '}
                        {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {evaluation.totalScore ? (
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                              {Math.round(parseFloat(evaluation.totalScore))}
                            </span>
                            <ScoreDisplay
                              score={percentageToScoreLevel(parseFloat(evaluation.totalScore))}
                              size="sm"
                              showLabel={true}
                            />
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${STATUS_CONFIG[evaluation.status].bgColor} ${STATUS_CONFIG[evaluation.status].color}`}>
                          {STATUS_CONFIG[evaluation.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          {evaluation.status !== 'final' && (
                            <button
                              onClick={() => setDeleteConfirmId(evaluation.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Beoordeling verwijderen"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/hr/beoordeling/${evaluation.id}`)}
                            className="px-2 py-1 text-sm text-blue-600 hover:text-blue-800"
                          >
                            Bekijken
                          </button>
                          {evaluation.status === 'submitted' && (
                            <button
                              onClick={() => updateStatus.mutateAsync({ id: evaluation.id, status: 'in_review' })}
                              disabled={updateStatus.isPending}
                              className="px-2 py-1 text-sm text-purple-600 hover:text-purple-800"
                            >
                              Review
                            </button>
                          )}
                          {evaluation.status === 'in_review' && (
                            <button
                              onClick={() => updateStatus.mutateAsync({ id: evaluation.id, status: 'final' })}
                              disabled={updateStatus.isPending}
                              className="px-2 py-1 text-sm text-green-600 hover:text-green-800"
                            >
                              Accorderen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredEvaluations.length === 0 && (
                <div className="p-8 text-center">
                  <p className="text-gray-500">Geen beoordelingen gevonden met deze filters.</p>
                </div>
              )}
            </div>

            {/* All Agreements Section */}
            <div id="agreements-section" className="mt-8">
              <AllAgreementsSection />
            </div>
          </>
        )}

        {/* === Verslagen Tab === */}
        {activeTab === 'verslagen' && (
          <>
            {/* Report Statistics */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                <p className="text-3xl font-bold text-gray-900">{reportStats.total}</p>
                <p className="text-sm text-gray-500">Totaal</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
                <p className="text-3xl font-bold text-yellow-700">{reportStats.concept}</p>
                <p className="text-sm text-yellow-600">Concept</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <p className="text-3xl font-bold text-blue-700">{reportStats.ingediend}</p>
                <p className="text-sm text-blue-600">Ingediend</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <p className="text-3xl font-bold text-green-700">{reportStats.definitief}</p>
                <p className="text-sm text-green-600">Definitief</p>
              </div>
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Status:</span>
                <select
                  value={reportStatusFilter}
                  onChange={(e) => setReportStatusFilter(e.target.value as AssessmentReportStatus | 'all')}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Alle</option>
                  <option value="concept">Concept</option>
                  <option value="ingediend">Ingediend</option>
                  <option value="definitief">Definitief</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Jaar:</span>
                <select
                  value={reportYearFilter}
                  onChange={(e) => setReportYearFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Alle</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Medewerker:</span>
                <input
                  type="text"
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  placeholder="Zoek op naam..."
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-48"
                />
              </div>
            </div>

            {/* Reports Table */}
            {reportsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Verslagen laden...</span>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Medewerker
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Periode
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                        Eindbeoordeling
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Aangemaakt door
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Datum
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Actie
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredReports.map((report) => {
                      const assessmentConfig = report.finalAssessment
                        ? ASSESSMENT_LABELS[report.finalAssessment]
                        : null;

                      return (
                        <tr
                          key={report.id}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => window.open(`/beoordelingsverslag/${report.id}`, '_blank')}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 text-xs font-medium">
                                  {report.employeeName
                                    ? report.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                                    : `M${report.employeeId}`}
                                </span>
                              </div>
                              <span className="text-sm font-medium text-gray-900">
                                {report.employeeName || `Medewerker #${report.employeeId}`}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatPeriod(report.periodeStart, report.periodeEnd)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${REPORT_STATUS_CONFIG[report.status].color}`}>
                              {REPORT_STATUS_CONFIG[report.status].label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {assessmentConfig ? (
                              <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${assessmentConfig.color}`}>
                                {assessmentConfig.label}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-sm">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {report.createdByName || `Gebruiker #${report.createdBy}`}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(report.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/beoordelingsverslag/${report.id}`, '_blank');
                              }}
                              className="px-2 py-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              Bekijken
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {filteredReports.length === 0 && (
                  <div className="p-8 text-center">
                    <p className="text-gray-500">Geen beoordelingsverslagen gevonden.</p>
                    {(reportStatusFilter !== 'all' || reportYearFilter !== 'all' || reportSearch) && (
                      <button
                        onClick={() => {
                          setReportStatusFilter('all');
                          setReportYearFilter('all');
                          setReportSearch('');
                        }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Filters wissen
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* === Werkroosters Tab === */}
        {activeTab === 'werkroosters' && (
          <OrgScheduleOverview />
        )}

        {/* === Nmbrs Tab === */}
        {activeTab === 'nmbrs' && (
          <div className="space-y-6">
            <NmbrsSyncStatus />

            {/* Nmbrs Sub-tabs */}
            <div className="border-b border-[#E2E8F0]">
              <nav className="flex gap-6">
                {([
                  { key: 'koppelingen', label: 'Koppelingen' },
                  { key: 'sync', label: 'Sync Status' },
                  { key: 'verlof', label: 'Verlof' },
                  { key: 'verzuim', label: 'Verzuim' },
                  { key: 'kalender', label: 'Kalender' },
                ] as const).map(({ key, label }) => (
                  <button
                    key={key}
                    className={`pb-2.5 text-sm font-medium border-b-2 transition-colors ${
                      nmbrsSubTab === key
                        ? 'border-[#2F5A7A] text-[#2F5A7A]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setNmbrsSubTab(key)}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Sub-tab Content */}
            {nmbrsSubTab === 'koppelingen' && (
              <div className="space-y-8">
                <NmbrsLinkPanel />
                <NmbrsUnlinkedPanel />
              </div>
            )}

            {nmbrsSubTab === 'sync' && (
              <div className="space-y-6">
                <NmbrsSyncDashboard />
                <NmbrsSyncHistory />
              </div>
            )}

            {nmbrsSubTab === 'verlof' && (
              <div className="space-y-6">
                <NmbrsLeaveLinkPanel />
                <NmbrsLeaveBalanceCard showAll />
                <NmbrsLeaveRequestForm />
                <NmbrsLeaveRequestList />
                <NmbrsLeaveOverview />
              </div>
            )}

            {nmbrsSubTab === 'verzuim' && (
              <NmbrsAbsenceOverview />
            )}

            {nmbrsSubTab === 'kalender' && (
              <NmbrsTeamCalendar />
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirmId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Beoordeling verwijderen</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Weet je zeker dat je deze beoordeling wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleDeleteEvaluation}
                  disabled={deleteEvaluation.isPending}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {deleteEvaluation.isPending ? 'Verwijderen...' : 'Verwijderen'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
